import { useState, type ButtonHTMLAttributes, type DragEvent, type KeyboardEvent } from 'react';
import { GripVertical } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Dragging a row of a list into a different place.
 *
 * The client's words: "where there is line edits like this, can you give
 * ability to drag up and down". Before this, reordering meant deleting a line
 * and retyping it at the bottom — and on a list of fifteen that is the reason
 * people stop maintaining it.
 *
 * Dragging is for the mouse and the handle is also a button: arrow keys move
 * the row while it has focus. That is not a nicety. HTML5 drag-and-drop fires
 * no events for a keyboard, so a list that can only be dragged is a list some
 * people cannot reorder at all.
 */
export function useReorder<T>(items: T[], onChange: (next: T[]) => void) {
  const [dragging, setDragging] = useState<number | null>(null);

  const move = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
      return;
    }

    const next = [...items];
    const [lifted] = next.splice(from, 1);

    if (lifted !== undefined) {
      next.splice(to, 0, lifted);
      onChange(next);
    }
  };

  /** Spread onto the row that should move. */
  const rowProps = (index: number) => ({
    onDragOver: (event: DragEvent) => event.preventDefault(),
    onDrop: (event: DragEvent) => {
      event.preventDefault();
      if (dragging !== null) move(dragging, index);
      setDragging(null);
    },
    'data-dragging': dragging === index ? '' : undefined,
  });

  /**
   * Spread onto the handle. `draggable` lives here rather than on the row so a
   * text input inside it still selects text instead of dragging the whole line.
   */
  const handleProps = (index: number, label: string) => ({
    draggable: true,
    onDragStart: () => setDragging(index),
    onDragEnd: () => setDragging(null),
    'aria-label': `Reorder ${label}. Use the arrow keys to move it.`,
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
        return;
      }

      event.preventDefault();
      const to = event.key === 'ArrowUp' ? index - 1 : index + 1;

      if (to < 0 || to >= items.length) {
        return;
      }

      move(index, to);

      /*
       * Follow the row. Moving something and then losing focus to wherever it
       * used to be makes a second move a hunt for the handle again.
       */
      const handles = (event.currentTarget as HTMLElement)
        .closest('[data-reorder-list]')
        ?.querySelectorAll<HTMLElement>('[data-reorder-handle]');

      requestAnimationFrame(() => handles?.[to]?.focus());
    },
  });

  return { dragging, move, rowProps, handleProps };
}

/**
 * The grip itself, so every list that can be reordered looks like one.
 *
 * Takes ordinary button props, because a caller sometimes needs more than
 * `handleProps` gives — a grid row that is also clickable has to stop the
 * click here, or picking the handle up selects the row underneath it.
 */
export function DragHandle({ className, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      data-reorder-handle
      className={cn(
        'flex h-8 w-5 shrink-0 cursor-grab items-center justify-center rounded text-muted/50 transition-colors',
        'hover:text-muted focus-visible:shadow-focus-navy focus-visible:outline-none active:cursor-grabbing',
        className,
      )}
      {...props}
    >
      <GripVertical aria-hidden className="h-4 w-4" />
    </button>
  );
}
